import { ExternalLink, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WebPageInfo } from '../types';

interface WebPagePreviewProps {
  webPage: WebPageInfo;
  isOutgoing?: boolean;
  className?: string;
}

/**
 * Компонент для отображения превью ссылки в сообщении
 */
export function WebPagePreview({ webPage, isOutgoing, className }: WebPagePreviewProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(webPage.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex gap-2 cursor-pointer rounded overflow-hidden group',
        isOutgoing ? 'hover:bg-white/10' : 'hover:bg-muted/50',
        className
      )}
    >
      {/* Left border accent */}
      <div
        className={cn(
          'w-0.5 flex-shrink-0 rounded-full',
          isOutgoing ? 'bg-white/50' : 'bg-primary'
        )}
      />

      {/* Content */}
      <div className="flex-1 min-w-0 py-1">
        {/* Site name */}
        {webPage.siteName && (
          <p
            className={cn(
              'text-xs font-medium truncate',
              isOutgoing ? 'text-white/80' : 'text-primary'
            )}
          >
            {webPage.siteName}
          </p>
        )}

        {/* Title */}
        {webPage.title && (
          <p className="text-sm font-medium line-clamp-2 mt-0.5">
            {webPage.title}
          </p>
        )}

        {/* Description */}
        {webPage.description && (
          <p
            className={cn(
              'text-xs line-clamp-2 mt-0.5',
              isOutgoing ? 'text-white/70' : 'text-muted-foreground'
            )}
          >
            {webPage.description}
          </p>
        )}

        {/* Video duration if available */}
        {webPage.duration && (
          <p
            className={cn(
              'text-xs mt-1',
              isOutgoing ? 'text-white/60' : 'text-muted-foreground'
            )}
          >
            {formatDuration(webPage.duration)}
          </p>
        )}

        {/* URL */}
        <div className="flex items-center gap-1 mt-1">
          <Globe
            className={cn(
              'h-3 w-3 flex-shrink-0',
              isOutgoing ? 'text-white/50' : 'text-muted-foreground'
            )}
          />
          <p
            className={cn(
              'text-[10px] truncate',
              isOutgoing ? 'text-white/50' : 'text-muted-foreground'
            )}
          >
            {webPage.displayUrl}
          </p>
          <ExternalLink
            className={cn(
              'h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity',
              isOutgoing ? 'text-white/70' : 'text-muted-foreground'
            )}
          />
        </div>
      </div>

      {/* Photo preview if available */}
      {webPage.photo && (
        <div
          className={cn(
            'w-14 h-14 flex-shrink-0 rounded overflow-hidden',
            isOutgoing ? 'bg-white/10' : 'bg-muted'
          )}
        >
          {/* Placeholder for photo - actual image would need to be loaded */}
          <div className="w-full h-full flex items-center justify-center">
            <Globe
              className={cn(
                'h-6 w-6',
                isOutgoing ? 'text-white/30' : 'text-muted-foreground/30'
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}
